/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/prop-types */
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useFormik } from 'formik';
import {
  CheckboxInput,
  // CheckboxGroup,
  CountrySelect, // Warning: React does not recognize the `activeContent` prop on a DOM element.
} from '@cochlear-design-system/foundation';

import { AddressInputWithFieldsView } from '../modes/AddressInputWithFieldsView.jsx';
import { buildConfig } from './buildConfig';

/**
 * @typedef {Object} AddressMaintenanceSingleEntryViewProps
 * @property {Array<Object>} [data]
 * @property {Object|string} activeItem
 * @property {Object.<string, string>} labels
 * @property {Object} config
 *@property {(formValues: Record<string, unknown>) => void} onChange
 */

/**
 * @type {React.ForwardRefExoticComponent<React.PropsWithoutRef<AddressMaintenanceSingleEntryViewProps> & React.RefAttributes<any>>}
 */

export const AddressMaintenanceSingleEntryView = forwardRef(
  (props, ref) => {
    const { activeItem, config, labels, onChange, data } = props;
    const singleConfig = buildConfig(config);

    const currentCountry = config.country;
    const readOnly = true;

    const childRef = useRef(null);

    const [country] = useState(currentCountry.code);
    const [countryName] = useState(currentCountry.name);

    const onSubmit = () => {
      // handleSaveHandle();
    };

    const onValidate = (values) => {
      const { countryStates } =
        singleConfig.addressGeneralConfig.addressInputConfig;
      const valuesState =
        values?.addressInput?.address?.state ||
        childRef.current.formik.values?.state;
      // Lookup state code using configCountryStates
      const matchingState = countryStates?.find(
        (state) =>
          state.label === valuesState || state.value === valuesState,
      );
      const stateCode = matchingState ? matchingState.value : '';
      const street =
        values?.addressInput?.address?.street ||
        childRef.current.formik.values?.street;

      const finalValues = {
        countryIso2Code: country,
        countryName,
        value: {
          id: values?.addressInput?.id,
          address: {
            addressText: values?.addressInput?.address.addressText,
            street: street ? [street] : [],
            city:
              values?.addressInput?.address?.city ||
              childRef.current.formik.values?.city,
            state: valuesState,
            stateCode,
            postalCode:
              values?.addressInput?.address?.postalCode ||
              childRef.current.formik.values?.postcode,
            postcode: '',
          },
          isPO:
            values?.addressInput?.address.isPO ||
            childRef.current.formik.values?.isPO,
        },
        isBilling: values.isBilling ? values.isBilling : false,
        isShipping: values?.isShipping ? values.isShipping : false,
        isMailing: values?.isMailing ? values.isMailing : false,
      };

      onChange(finalValues);
    };

    const formik = useFormik({
      enableReinitialize: true,
      initialValues: readOnly
        ? {
            country: activeItem?.countryIso2Code,
            isBilling: activeItem?.isBilling,
            isMailing: activeItem?.isMailing,
            isShipping: activeItem?.isShipping,
            id: activeItem?.value?.id,
          }
        : {},
      validate: (values) => onValidate(values),
      onSubmit,
      initialTouched: readOnly
        ? {
            country: true,
            isBilling: true,
            isMailing: true,
            isShipping: true,
          }
        : {},
    });

    const doValidateForm = (displayErrors) => {
      const errors = {};

      // Ensure at least one checkbox is selected
      if (
        !formik.values.isBilling &&
        !formik.values.isMailing &&
        !formik.values.isShipping
      ) {
        errors.checkboxes =
          labels[
            'labels.address.edit.addressType.validation.required'
          ];
      }

      // Perform other validations (e.g., address validation)
      const childErrors =
        childRef.current.doValidateForm(displayErrors);

      // Combine errors from checkboxes and child validation
      const combinedErrors = { ...childErrors, ...errors };

      // If displayErrors is true, set the errors in Formik
      if (displayErrors) {
        formik.setErrors(combinedErrors);
      }

      // Return the combined errors
      return combinedErrors;
    };

    useEffect(() => {
      const resetFormAndSetCountry = async () => {
        formik.resetForm();
        await formik.setFieldValue('country', currentCountry.code);
      };
      resetFormAndSetCountry().catch((error) => {
        console.error(
          'Error resetting form and setting country:',
          error,
        );
      });
    }, []);

    // const cssFormInput = 'ccl__form-input';
    const cssContentModifier =
      'address-maintenance-single-entry-view';

    const checkBoxFields = [
      {
        id: 'isBilling',
        name: 'isBilling',
        apifield: 'isBilling',
        label: 'labels.address.edit.isBilling.label',
        optionalText: 'labels.address.edit.currentAddressType.label',
      },
      {
        id: 'isMailing',
        name: 'isMailing',
        apifield: 'isMailing',
        label: 'labels.address.edit.isMailing.label',
        optionalText: 'labels.address.edit.currentAddressType.label',
      },
      {
        id: 'isShipping',
        name: 'isShipping',
        apifield: 'isShipping',
        label: 'labels.address.edit.isShipping.label',
        optionalText: 'labels.address.edit.currentAddressType.label',
      },
    ];

    useImperativeHandle(ref, () => ({
      doValidateForm,
      getFormData: () => {
        // Get the latest form values
        const errors = doValidateForm(false);
        const isValid = Object.keys(errors).length === 0;

        if (isValid) {
          // Call onValidate to get the final formatted values
          onValidate(formik.values);

          // Return the current form state and validation status
          return {
            values: formik.values,
            isValid,
            formattedData: null, // Will be set via onChange
          };
        }

        return {
          values: formik.values,
          isValid: false,
          errors,
        };
      },
      submitForm: () => {
        const errors = doValidateForm(true); // Show errors
        const isValid = Object.keys(errors).length === 0;

        if (isValid) {
          // Trigger validation which calls onChange with formatted data
          onValidate(formik.values);
          return {
            success: true,
            values: formik.values,
          };
        }

        return {
          success: false,
          errors,
        };
      },
      resetForm: () => {
        formik.resetForm();
      },
    }));

    return (
      <>
        <div>
          <div className={`${cssContentModifier}-content `}>
            {config.countrySelector?.customData?.hideCountry ===
              true && (
              <CountrySelect
                customData={
                  singleConfig.addressGeneralConfig
                    .countrySelectConfig.customData
                }
                label={labels['labels.address.edit.country.label']}
                name="country"
                id="country"
                size="large"
                showISDN={false}
                showISO={false}
                showResultsISDN={false}
                showResultsISO={false}
                isSearchable
                defaultValue={country}
                maxResults={6}
                value={formik.values.country}
                lockCountry={
                  singleConfig.addressGeneralConfig
                    .countrySelectConfig.lockCountry
                }
              />
            )}
            <AddressInputWithFieldsView
              ref={childRef}
              config={
                singleConfig.addressGeneralConfig.addressInputConfig
              }
              id={
                singleConfig.addressGeneralConfig.addressInputConfig
                  ?.id
              }
              error={
                singleConfig.addressGeneralConfig.addressInputConfig
                  ?.error
              }
              errorMsg={
                singleConfig.addressGeneralConfig.addressInputConfig
                  ?.errorMsg
              }
              setFocus={
                singleConfig.addressGeneralConfig.addressInputConfig
                  ?.setFocus
              }
              disabled={
                singleConfig.addressGeneralConfig.addressInputConfig
                  ?.disabled
              }
              state
              Managed={
                singleConfig.addressGeneralConfig.addressInputConfig
                  ?.stateManaged
              }
              name={
                singleConfig.addressGeneralConfig.addressInputConfig
                  ?.name
              }
              hint={
                singleConfig.addressGeneralConfig.addressInputConfig
                  ?.hint
              }
              optionalText={
                singleConfig.addressGeneralConfig.addressInputConfig
                  ?.optionalText
              }
              promptText={
                singleConfig.addressGeneralConfig.addressInputConfig
                  ?.prompt
              }
              promptMobile={
                singleConfig.addressGeneralConfig.addressInputConfig
                  ?.promptMobile
              }
              loading={
                singleConfig.addressGeneralConfig.addressInputConfig
                  ?.loading
              }
              addressNotFoundText={
                singleConfig.addressGeneralConfig.addressInputConfig
                  ?.addressNotFoundText
              }
              addressNotFoundButton={
                singleConfig.addressGeneralConfig.addressInputConfig
                  ?.addressNotFoundButton
              }
              lookupErrorLabel={
                singleConfig.addressGeneralConfig.addressInputConfig
                  ?.lookupErrorLabel
              }
              label={
                singleConfig.addressGeneralConfig.addressInputConfig
                  ?.label
              }
              value={activeItem?.value}
              currentCountry={country}
              labels={labels}
              disableLiveValidation={
                singleConfig.addressGeneralConfig.addressInputConfig
                  ?.disableLiveValidation
              }
              onChange={(e) => {
                if (e.dirty && JSON.stringify(e.errors) === '{}') {
                  formik.handleChange({
                    target: {
                      name: `${singleConfig.addressGeneralConfig.addressInputConfig?.id}`,
                      value: {
                        address: {
                          ...e.values,
                          postalCode: e.values.postcode,
                        },
                        id: activeItem?.value?.id,
                        isPO: e.values.isPO,
                      },
                    },
                  });
                } else {
                  formik.setErrors(e.errors);
                }
              }}
            />

            <div className="address-type-checkboxes">
              {/* Group label */}
              <div className="ccl__text--body-text-bold">
                {labels[singleConfig.addressGeneralConfig.setDefault]}
              </div>

              {/* Error message */}
              {Object.keys(formik.errors).length === 1 &&
                formik.errors.checkboxes && (
                  <div
                    className="ccl-e-validation-message ccl-e-validation-message--error"
                    style={{
                      color: '#d32f2f',
                      fontSize: '12px',
                      marginBottom: '8px',
                    }}
                  >
                    {formik.errors.checkboxes}
                  </div>
                )}

              <div>
                {checkBoxFields.map((field) => (
                  <CheckboxInput
                    key={field.id}
                    id={field.id}
                    name={field.name}
                    className="ccl__form-input__address-type"
                    label={`${labels[field.label]}${
                      data?.filter(
                        (x) => x[field.apifield] === true,
                      )[0]
                        ? ` (${labels[field.optionalText]} ${
                            data.filter(
                              (x) => x[field.apifield] === true,
                            )[0]?.value.address.street
                          }...)`
                        : ''
                    }`}
                    checked={Boolean(formik.values[field.apifield])}
                    disabled={Boolean(
                      formik.initialValues[field.apifield],
                    )}
                    value={Boolean(formik.values[field.apifield])}
                    onChange={(e) => {
                      const isChecked = e.target.checked;

                      // Update Formik state
                      formik.setFieldValue(field.apifield, isChecked);

                      // Clear checkbox group errors when user selects an option
                      if (formik.errors.checkboxes && isChecked) {
                        formik.setFieldError('checkboxes', undefined);
                      }

                      // Trigger validation after state update
                      setTimeout(() => {
                        const updatedValues = {
                          ...formik.values,
                          [field.apifield]: isChecked,
                          // Ensure addressInput with id is included
                          addressInput: formik.values
                            .addressInput || {
                            id: activeItem?.value?.id,
                            address: activeItem?.value?.address || {},
                            isPO: activeItem?.value?.isPO || false,
                          },
                        };
                        onValidate(updatedValues);
                      }, 0);
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </>
    );
  },
);
